import { Component, computed, effect, ElementRef, inject, input, signal, untracked, viewChild, WritableSignal } from '@angular/core';
import { routeParam } from '@/utils/signals/router';
import { loader } from '@/utils/signals/loader';
import {
  BothTimeSeriesDto,
  DataPointDto,
  DebugSeriesDto,
  SensorBlockService,
  SensorStatisticService,
  TimeSeriesDto,
  TimeSeriesFilterDto
} from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { DateRangeObject } from '@models/date-range';
import { toValidDate } from '@/utils/date-utils';
import {
  AGGREGATION_INTERVAL_LABELS
} from '@shared/form-controls/aggregation-interval-select/aggregation-interval-select.component';
import * as Plotly from 'plotly.js-dist-min';
import { map } from 'rxjs';
import AggregationIntervalEnum = TimeSeriesFilterDto.AggregationIntervalEnum;

const UNBINNED_MAX_HITS = 1000;

interface SeriesPair {
  raw?: TimeSeriesDto;
  cleaned?: TimeSeriesDto;
}

function splitBothSeries(both: BothTimeSeriesDto): SeriesPair {
  const shared = {
    sensorItemDto: both.sensorItemDto,
    effectiveAggregationInterval: both.effectiveAggregationInterval,
    truncated: both.truncated,
  };
  return {
    raw: { ...shared, dataPoints: both.rawDataPoints },
    cleaned: { ...shared, dataPoints: both.cleanedDataPoints },
  };
}

/**
 * The ErrorInterceptor rethrows the unwrapped body, so anything that is not an object with a
 * backend-authored `detail`/`message` is a raw English HttpErrorResponse string and must not
 * reach the German banner.
 */
function backendDetail(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const body = error as { detail?: unknown; message?: unknown };
  const detail = body.detail ?? body.message;
  return typeof detail === 'string' ? detail.trim() : '';
}

function toExactIsoRange(range: DateRangeObject | undefined): { startTime: string; endTime: string } | undefined {
  const start = toValidDate(range?.start);
  const end = toValidDate(range?.end);
  if (!start || !end) return undefined;
  return { startTime: start.toISOString(), endTime: end.toISOString() };
}

function intervalLabel(interval: AggregationIntervalEnum | undefined): string {
  return interval ? AGGREGATION_INTERVAL_LABELS[interval] ?? interval : '';
}

@Component({
  standalone: false,
  selector: 'wr-sensor-block-chart-view',
  templateUrl: './sensor-block-chart-page.component.html',
  styleUrl: './sensor-block-chart-page.component.scss'
})
export class SensorBlockChartPageComponent {
  readonly sensorBlockService = inject(SensorBlockService);
  readonly sensorStatisticService = inject(SensorStatisticService);

  readonly embedded = input(false);

  public graph = {
    data: [] as Plotly.Data[],
    layout: {
      autosize: true,
      xaxis: { type: 'date', tickangle: 0, automargin: true },
      yaxis: { automargin: true },
      margin: { t: 20, b: 50, l: 60, r: 30 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      legend: { orientation: 'h', y: -0.15 }
    }
  };

  readonly chartConfig = {
    responsive: true,
  };

  readonly plotRef = viewChild('plot', { read: ElementRef });
  readonly chartReady = signal(false);
  private readyTimer?: ReturnType<typeof setTimeout>;

  loader = loader();
  readonly seriesLoader = loader();
  readonly dateRange: WritableSignal<DateRangeObject | undefined> = signal({
    start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
    end: new Date(),
  });
  readonly aggregationInterval = signal(AggregationIntervalEnum.Hour);
  readonly chartType = signal('line+points');
  readonly source = signal<'raw' | 'cleaned' | 'both'>('both');
  readonly showImputed = signal(true);
  /** Holds the backend detail of the last failure, `''` when it failed without one, `undefined` when fine. */
  readonly seriesError = signal<string | undefined>(undefined);
  readonly debugError = signal<string | undefined>(undefined);
  readonly loadError = computed(() => {
    const source = this.source();
    const failed = [
      { series: source === 'both' ? 'RAW, CLEANED' : source.toUpperCase(), detail: this.seriesError() },
      { series: 'Berechnete Werte', detail: this.debugError() },
    ].filter(entry => entry.detail !== undefined);
    if (failed.length === 0) return undefined;
    const details = [...new Set(failed.map(entry => entry.detail).filter(detail => !!detail))];
    return `Sensordaten (${failed.map(entry => entry.series).join(', ')}) konnten nicht geladen werden.`
      + (details.length > 0 ? ` ${details.join(' ')}` : '');
  });
  readonly sensorBlockId = routeParam('id', true);
  readonly sensorBlockItem = asyncComputed(() =>
    this.sensorBlockService.findById(this.sensorBlockId()), undefined, this.loader);

  readonly selectedSensorId: WritableSignal<string | undefined> = signal(undefined);

  private readonly isoRange = computed(() => toExactIsoRange(this.dateRange()));

  /**
   * One request per selection: in "both" mode the backend decides the aggregation interval once
   * for both indices (`findDataBySensorBoth`). Two independent `findDataBySensor` calls would each
   * coarsen against their own data extent — RAW hourly next to CLEANED per minute on the same axis.
   */
  readonly series = asyncComputed<SeriesPair | undefined>(() => {
    const selectedSensorId = this.selectedSensorId();
    const range = this.isoRange();
    const aggregationInterval = this.aggregationInterval();
    const source = this.source();
    this.seriesError.set(undefined);
    if (!selectedSensorId || !range) return undefined;
    const filter: TimeSeriesFilterDto = {
      sensorId: selectedSensorId,
      startTime: range.startTime,
      endTime: range.endTime,
      aggregationInterval: aggregationInterval,
    };
    if (source === 'both') {
      return this.sensorStatisticService.findDataBySensorBoth(filter).pipe(map(splitBothSeries));
    }
    return this.sensorStatisticService.findDataBySensor({ ...filter, source })
      .pipe(map(series => source === 'raw' ? { raw: series } : { cleaned: series }));
  }, undefined, {
    isLoading: this.seriesLoader,
    onError: (error) => this.seriesError.set(backendDetail(error)),
  });

  readonly rawSeries = computed(() => this.series()?.raw);
  readonly cleanedSeries = computed(() => this.series()?.cleaned);

  readonly debugSeries = asyncComputed<DebugSeriesDto | undefined>(() => {
    const selectedSensorId = this.selectedSensorId();
    const range = this.isoRange();
    const wantsImputed = this.showImputed();
    this.debugError.set(undefined);
    if (!wantsImputed || !selectedSensorId || !range) return undefined;
    return this.sensorStatisticService.debugPointsBySensor({
      sensorId: selectedSensorId,
      startTime: range.startTime,
      endTime: range.endTime,
      aggregationInterval: undefined
    });
  }, undefined, {
    isLoading: this.loader,
    onError: (error) => this.debugError.set(backendDetail(error)),
  });

  private readonly coarsenedInterval = computed(() => {
    const requested = this.aggregationInterval();
    if (!requested) return undefined;
    return [this.rawSeries()?.effectiveAggregationInterval, this.cleanedSeries()?.effectiveAggregationInterval]
      .find(interval => !!interval && interval !== requested);
  });

  readonly notices = computed(() => {
    if (this.seriesLoader()) return [];
    const messages: string[] = [];
    const coarsened = this.coarsenedInterval();
    if (coarsened) {
      messages.push(`Das Intervall „${intervalLabel(this.aggregationInterval())}“ ergibt für den gewählten `
        + `Zeitraum zu viele Datenpunkte. Die Daten werden stattdessen in „${intervalLabel(coarsened)}“ aggregiert.`);
    }
    if (this.rawSeries()?.truncated || this.cleanedSeries()?.truncated) {
      messages.push(`Ohne Aggregation werden nur die ältesten ${UNBINNED_MAX_HITS} Messwerte des Zeitraums geladen. `
        + 'Für den gesamten Zeitraum bitte ein Aggregationsintervall wählen.');
    }
    if (this.debugSeries()?.truncated) {
      messages.push('Die Marker für berechnete Werte und Ausreißer decken nur die ältesten '
        + `${UNBINNED_MAX_HITS} Messwerte des Zeitraums ab.`);
    }
    return messages;
  });

  readonly pointCount = computed(() => {
    const src = this.source();
    const raw = this.rawSeries();
    const cleaned = this.cleanedSeries();
    const r = raw ? raw.dataPoints.length : 0;
    const c = cleaned ? cleaned.dataPoints.length : 0;
    if (src === 'both') return `RAW: ${r} | CLEANED: ${c}`;
    if (src === 'raw') return `${r}`;
    return `${c}`;
  });

  readonly isEmpty = computed(() => {
    const src = this.source();
    const raw = this.rawSeries();
    const cleaned = this.cleanedSeries();
    const r = raw?.dataPoints.length ?? 0;
    const c = cleaned?.dataPoints.length ?? 0;
    if (src === 'raw') return r === 0;
    if (src === 'cleaned') return c === 0;
    return r === 0 && c === 0;
  });

  constructor() {
    effect((onCleanup) => {
      const ref = this.plotRef() as ElementRef<HTMLElement> | undefined;
      if (!ref?.nativeElement) return;

      const host = ref.nativeElement;
      const observer = new ResizeObserver(() => this.tryResize());
      observer.observe(host);
      onCleanup(() => {
        observer.disconnect();
        if (this.readyTimer) clearTimeout(this.readyTimer);
        this.readyTimer = undefined;
        this.chartReady.set(false);
      });
    });

    effect(() => {
      const sensorBlock = this.sensorBlockItem();
      if (!sensorBlock) {
        return;
      }
      const current = untracked(this.selectedSensorId);
      if (!current || !sensorBlock.sensors.some((sensor) => sensor.id === current)) {
        this.selectedSensorId.set(sensorBlock.sensors[0]?.id);
      }
    });
    effect(() => {
      const src = this.source();
      const chartType = this.chartType();
      const raw = this.rawSeries();
      const cleaned = this.cleanedSeries();
      const debug = this.debugSeries();

      let type: 'scattergl' | 'bar' = 'scattergl';
      let mode: 'lines' | 'markers' | 'lines+markers' | undefined = undefined;

      switch (chartType) {
        case 'line':
          type = 'scattergl';
          mode = 'lines';
          break;
        case 'line+points':
          type = 'scattergl';
          mode = 'lines+markers';
          break;
        case 'scatter':
          type = 'scattergl';
          mode = 'markers';
          break;
        case 'bar':
          type = 'bar';
          mode = undefined;
          break;
      }

      const traces: Plotly.Data[] = [];

      const toX = (items: DataPointDto[]) => items.map(i => i.timestamp);
      const toY = (items: DataPointDto[]) => items.map(i => i.value ?? null);

      if (src === 'raw' || src === 'both') {
        if (raw && raw.dataPoints.length > 0) {
          traces.push({
            x: toX(raw.dataPoints),
            y: toY(raw.dataPoints),
            type,
            mode,
            name: 'RAW',
            line: { color: '#6baed6', width: 1.5 },
            marker: { color: '#6baed6', size: 6 },
            connectgaps: false
          });
        }
      }

      if (src === 'cleaned' || src === 'both') {
        if (cleaned && cleaned.dataPoints.length > 0) {
          traces.push({
            x: toX(cleaned.dataPoints),
            y: toY(cleaned.dataPoints),
            type,
            mode,
            name: 'CLEANED',
            line: { color: '#2ca02c', width: 2 },
            marker: { color: '#2ca02c', size: 6 },
            connectgaps: false
          });
        }
      }

      if (this.showImputed() && debug) {
        const cleanedDocs = debug.cleanedDocs;
        const imputed = cleanedDocs.filter(d => d.isImputed);
        if (imputed.length > 0) {
          traces.push({
            x: imputed.map(d => d.timestamp),
            y: imputed.map(d => d.cleanedValue ?? null),
            type: 'scatter',
            mode: 'markers',
            name: 'Calculated (imputed)',
            marker: { color: '#ff7f0e', size: 9, symbol: 'diamond' }
          });
        }

        const flaggedOnly = cleanedDocs.filter(d => d.isOutlier && !d.isImputed);
        if (flaggedOnly.length > 0) {
          traces.push({
            x: flaggedOnly.map(d => d.timestamp),
            y: flaggedOnly.map(d => d.cleanedValue ?? null),
            type: 'scatter',
            mode: 'markers',
            name: 'Flagged outlier',
            marker: { color: 'rgba(255,0,0,0)', line: { color: '#d62728', width: 1.5 }, size: 9, symbol: 'circle-open' }
          });
        }
      }

      this.graph.data = traces;
    });
  }

  refresh(): void {
    this.sensorBlockItem.reload();
    this.series.reload();
    this.debugSeries.reload();
  }

  navigateBack(): void {
    window.history.back();
  }

  onPlotInit(): void {
    [0, 50, 200, 500, 1000].forEach(delay =>
      setTimeout(() => this.tryResize(), delay),
    );
  }

  private tryResize(): void {
    const host = this.plotRef()?.nativeElement;
    if (!host) return;
    const plotDiv = (host.classList.contains('js-plotly-plot')
      ? host
      : host.querySelector('.js-plotly-plot')) as HTMLElement | null;
    if (!plotDiv) return;
    try {
      Plotly.Plots.resize(plotDiv);
      if (this.readyTimer) clearTimeout(this.readyTimer);
      this.readyTimer = setTimeout(() => this.chartReady.set(true), 200);
    } catch {
      return;
    }
  }
}
